<template>
    <div class="afu-border afu-border-t-0 afu-border-l-0 afu-border-r-0 afu-border-b-1 afu-border-solid afu-border-neutral afu-py-7">
        <h3 class="afu-text-4xl afu-text-secondary afu-mb-2 afu-font-semibold">
            {{ $_seekerFieldByCode('reset_password').label || $t('account.reset_password') }}
        </h3>

        <p class="afu-text-black afu-mb-7">
            {{ $t('account.reset_password_info') }}
        </p>

        <C1Alert
            v-if="!!passwordResponse.message"
            :variant="passwordResponse.variant"
        >
            <div><strong>{{ passwordResponse.message_title }}</strong></div>
            <span v-html="passwordResponse.message" />

            <div v-if="isResettingPassword">
                <a
                    href=""
                    class="resend-code-link"
                    @click.stop.prevent="resendCode()"
                >
                    <C1Spinner v-if="loader.resend_code" /> {{ $t('account.resend_code') }}.
                </a>
            </div>
        </C1Alert>

        <div
            v-if="isResettingPassword"
            class="md:afu-w-1/2"
        >
            <ValidationObserver
                ref="passwordResetForm"
                tag="form"
                @submit.stop.prevent="resetPassword"
            >
                <ValidationProvider
                    name="reset code"
                    rules="required"
                    v-slot="{ errors }"
                >
                    <C1FormGroup>
                        <C1FormInput
                            v-model="code"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                            :disabled="$_seekerFieldByCode('reset_password').read_only"
                            name="code"
                            :placeholder="$t('label.enter_code')"
                        />
                    </C1FormGroup>
                </ValidationProvider>
                <ValidationProvider
                    name="Password"
                    rules="required|verify_password"
                    v-slot="{ errors }"
                >
                    <C1FormGroup>
                        <C1FormInput
                            type="password"
                            v-model="newPassword"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                            :placeholder="$t('label.enter_new_password')"
                            :disabled="$_seekerFieldByCode('reset_password').read_only"
                        />
                    </C1FormGroup>
                </ValidationProvider>

                <C1Button
                    :disabled="loader.reset_password || $_seekerFieldByCode('reset_password').read_only"
                    :is-busy="loader.reset_password"
                    variant="outline-primary"
                    block
                    @click="resetPassword()"
                >
                    {{ $t('label.reset_password') }}
                </C1Button>
            </ValidationObserver>
        </div>

        <template v-else>
            <C1Button
                :disabled="loader.update_password || $_seekerFieldByCode('reset_password').read_only"
                :is-busy="loader.update_password"
                variant="outline-secondary"
                @click="updatePassword()"
            >
                {{ $t('label.email_me_code') }}
            </C1Button>
        </template>
    </div>
</template>

<script>
    import has from 'lodash/has';
    import { mapState, mapActions, mapGetters } from 'vuex';
    import C1Button from '@/components/C1Components/C1Button';
    import C1Alert from '@/components/C1Components/C1Alert';
    import C1FormInput from '@/components/C1Components/C1FormInput';
    import C1FormGroup from '@/components/C1Components/C1FormGroup';
    import C1Spinner from '@/components/C1Components/C1Spinner';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';

    export default {
        name: 'SettingUpdatePassword',

        components: {
            C1Spinner,
            C1FormGroup,
            C1FormInput,
            C1Alert,
            C1Button,
            ValidationObserver,
            ValidationProvider,
        },

        data() {
            return {
                newPassword: '',
                code: '',
                isResettingPassword: false,
                passwordResponse: {
                    message_title: '',
                    message: '',
                    variant: '',
                },
                loader: {
                    update_password: false,
                    resend_code: false,
                    reset_password: false,
                },
            };
        },

        computed: {
            ...mapState({
                $_me: state => state.me.user,
                $_apiMessage: state => state.apiMessage,
            }),

            ...mapGetters('site', {
                $_seekerFieldByCode: 'seekerFieldByCode',
            }),
        },

        methods: {
            ...mapActions('auth', {
                $_forgotPassword: 'forgotPassword',
                $_resetPassword: 'resetPassword',
            }),

            updatePassword() {
                const data = {
                    email: this.$_me.email,
                    code: true,
                };

                this.loader.update_password = true;
                this.$_forgotPassword(data)
                    .then(() => {
                        this.passwordResponse.message_title = '';
                        this.passwordResponse.message =
                            'We have sent a reset code to your current email. Please make sure to also check your spam folder.';
                        this.passwordResponse.variant = 'success';
                        this.isResettingPassword = true;
                    })
                    .catch(error => {
                        const errorCode = has(error, 'response.data.error')
                            ? error.response.data.error
                            : 'ServerErrorException';
                        this.passwordResponse.message_title =
                            'Update password failed';
                        this.passwordResponse.message = this.$_apiMessage[
                            errorCode
                        ];
                        this.passwordResponse.variant = 'danger';
                    })
                    .finally(() => {
                        this.loader.update_password = false;
                    });
            },

            resendCode() {
                if (this.loader.resend_code) {
                    return false;
                }

                const data = {
                    email: this.$_me.email,
                    code: true,
                };

                this.loader.resend_code = true;
                this.$_forgotPassword(data)
                    .then(() => {
                        this.passwordResponse.message_title = '';
                        this.passwordResponse.variant = 'success';
                        this.passwordResponse.message =
                            'We have sent a reset code to your current email. Please make sure to also check your spam folder.';
                    })
                    .catch(error => {
                        const errorCode = has(error, 'response.data.error')
                            ? error.response.data.error
                            : 'ServerErrorException';
                        this.passwordResponse.message_title =
                            'Resend password code failed';
                        this.passwordResponse.message = this.$_apiMessage[
                            errorCode
                        ];
                        this.passwordResponse.variant = 'danger';
                    })
                    .finally(() => {
                        this.loader.resend_code = false;
                    });
            },

            async resetPassword() {
                const validate = await this.$refs.passwordResetForm.validate();
                if (!validate) {
                    return;
                }

                this.resetPasswordResponse();

                const data = {
                    email: this.$_me.email,
                    password: this.newPassword,
                    code: this.code,
                };

                this.loader.reset_password = true;
                this.$_resetPassword(data)
                    .then(() => {
                        this.passwordResponse.message_title = '';
                        this.passwordResponse.variant = 'success';
                        this.passwordResponse.message =
                            'Your password has successfully been updated.';
                        this.isResettingPassword = false;
                        this.$nextTick(() => {
                            this.code = '';
                            this.newPassword = '';
                        });
                    })
                    .catch(error => {
                        const errorCode = has(error, 'response.data.error')
                            ? error.response.data.error
                            : 'ServerErrorException';
                        this.passwordResponse.message_title =
                            'Reset password failed';
                        this.passwordResponse.message = this.$_apiMessage[
                            errorCode
                        ];
                        this.passwordResponse.variant = 'danger';
                    })
                    .finally(() => {
                        this.loader.reset_password = false;
                    });
            },

            resetPasswordResponse() {
                this.passwordResponse.message_title = '';
                this.passwordResponse.message = '';
                this.passwordResponse.variant = '';
            },
        },
    };
</script>

<style
    lang="scss"
    scoped
>
    .resend-code-link {
        color: #3b6028;
        font-size: 12px;
        font-weight: 700;
    }
</style>
